import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, Sparkles } from "lucide-react";

// Shown on the dashboard only when the signed-in user has zero uploads.
// Aims to remove the "did I miss a step?" anxiety + offer a sample report
// as a soft-onramp before they commit to uploading their own PDF.
//
// We render this as the very first card above the Reports list so it's
// the first thing a stuck user sees on every visit. Dismissable via a
// localStorage flag — once dismissed it stays hidden even if the user
// keeps not uploading, because we've already nudged them and we trust
// the drip email sequence to take over from there.
export default function NoUploadBanner() {
    const dismissed = (() => {
        try {
            return localStorage.getItem("bl_no_upload_banner_dismissed") === "1";
        } catch {
            return false;
        }
    })();
    if (dismissed) return null;

    const dismiss = () => {
        try {
            localStorage.setItem("bl_no_upload_banner_dismissed", "1");
        } catch { /* non-critical */ }
        // re-render via a synthetic event the parent listens for
        window.dispatchEvent(new Event("bl-no-upload-banner-dismissed"));
    };

    return (
        <div
            className="relative overflow-hidden rounded-xl border border-[var(--bl-teal)]/30 bg-[var(--bl-teal-tint)] p-5 sm:p-6 mb-3"
            data-testid="dashboard-no-upload-banner"
        >
            <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-10 h-10 rounded-lg bg-[var(--bl-teal)]/15 items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-[var(--bl-teal)]" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="bl-eyebrow text-[var(--bl-teal)]">
                        <Sparkles className="inline w-3 h-3 -mt-0.5 mr-1" aria-hidden />
                        Get started in 90 seconds
                    </div>
                    <h3 className="mt-1 font-display font-semibold text-xl text-bl">
                        You haven&apos;t uploaded an inspection yet.
                    </h3>
                    <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                        Drop your inspector&apos;s PDF and we&apos;ll have your
                        priority-ranked repair list, localized cost estimates,
                        and negotiation script ready in minutes. Or see what a
                        finished report looks like first.
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                        <Link
                            to="/upload?source=dashboard_banner"
                            className="bl-btn-primary"
                            data-testid="dashboard-no-upload-banner-upload-btn"
                        >
                            Upload my inspection
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/sample-report?source=dashboard_banner"
                            className="bl-btn-ghost"
                            data-testid="dashboard-no-upload-banner-sample-btn"
                        >
                            See a sample report →
                        </Link>
                        <button
                            type="button"
                            onClick={dismiss}
                            className="ml-auto text-xs text-bl-muted underline"
                            data-testid="dashboard-no-upload-banner-dismiss"
                        >
                            Hide for now
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
